import { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@shadcn/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@shadcn/components/ui/table";

import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@shadcn/components/ui/select";
import { Label } from "@shadcn/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn/components/ui/tabs";
import { Download, BarChart2, PieChart, DollarSign, Package, BarChart } from "lucide-react";
import { toast, ToastContainer } from 'react-toastify';
import superagent from 'superagent';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { backendUrl } from '../../../helpers/consts';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function ShipmentAnalyticsReport() {
  const [data, setData] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    country: 'all',
    carrier: 'all'
  });
  const [countries, setCountries] = useState([]);
  const [carriers, setCarriers] = useState([]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchData(),
          fetchQuoteData()
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await superagent.get(`${backendUrl}/shipmentAnalytics/onTimeRate`)
      .set("Authorization", `Bearer ${localStorage.getItem("authJWT")}`)
      .set("accept", "json")
      .set("Content-Type", "application/json");
      setData(response.body);
      
      // Extract unique countries and carriers
      const uniqueCountries = new Set();
      const uniqueCarriers = new Set();
      
      response.body.byCountry.forEach(item => {
        uniqueCountries.add(item.country);
        
      });

      response.body.byCarrier.forEach(item => {
        uniqueCarriers.add(item.carrier);
      });
      
      setCountries(Array.from(uniqueCountries));
      setCarriers(Array.from(uniqueCarriers));
    } catch (error) {
      toast.error("Failed to fetch shipment analytics data");
    }
  };

  const fetchQuoteData = async () => {
    try {
      const response = await superagent.post(`${backendUrl}/orders/calculateAverageQuotePrice`)
      .set("Authorization", `Bearer ${localStorage.getItem("authJWT")}`)
      .set("accept", "json")
      .set("Content-Type", "application/json");
      setQuoteData(response.body);
    } catch (error) {
      toast.error("Failed to fetch quote price data");
    }
  };

  const filteredData = useMemo(() => {
    if (!data) return null;

    let byCarrierFiltered = [...data.byCarrier];
    let byCountryFiltered = [...data.byCountry];

    if (filters.carrier !== 'all') {
      byCarrierFiltered = byCarrierFiltered.filter(item => item.carrier === filters.carrier);
      
    }

    if (filters.country !== 'all') {
      byCountryFiltered = byCountryFiltered.filter(item => item.country === filters.country);
    }

    return {
      overall: data.overall,
      byCarrier: byCarrierFiltered,
      byCountry: byCountryFiltered
    };
  }, [data, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  

  // Chart data for carrier performance
  const carrierChartData = useMemo(() => {
    if (!filteredData) return null;
    
    return {
      labels: filteredData.byCarrier.map(item => item.carrier),
      datasets: [
        {
          label: 'On-Time Rate (%)',
          data: filteredData.byCarrier.map(item => item.onTimeRate),
          backgroundColor: 'rgba(53, 162, 235, 0.5)',
          borderColor: 'rgba(53, 162, 235, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [filteredData]);

  // Chart data for country performance
  const countryChartData = useMemo(() => {
    if (!filteredData || filteredData.byCountry.length === 0) return null;
    
    return {
      labels: filteredData.byCountry.map(item => item.country),
      datasets: [
        {
          label: 'On-Time Rate (%)',
          data: filteredData.byCountry.map(item => item.onTimeRate),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [filteredData]);

  // Chart data for delivery volume
  const volumePieChartData = useMemo(() => {
    if (!filteredData) return null;
    
    return {
      labels: filteredData.byCarrier.map(item => item.carrier),
      datasets: [
        {
          label: 'Total Delivered',
          data: filteredData.byCarrier.map(item => item.totalDelivered),
          backgroundColor: [
            'rgba(255, 99, 132, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 206, 86, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)',
            'rgba(255, 159, 64, 0.5)',
            'rgba(199, 199, 199, 0.5)',
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(199, 199, 199, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  }, [filteredData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'On-Time Rate (%)'
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Carrier Performance',
      },
    },
  };

  return (
    <div className='page-content'>
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Shipment Analytics Report</CardTitle>
            <CardDescription>
              View on-time delivery performance metrics by carrier and country
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading data...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Quote Price KPI Cards */}
                {quoteData && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-700 flex items-center">
                          <DollarSign className="h-5 w-5 mr-2 text-blue-500" />
                          Total Quote Price For Shippit and EIZ provider
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-700">${quoteData.totalQuotePrice.toFixed(2)}</div>
                        <p className="text-sm text-gray-500 mt-1">💰 Total value of all quotes</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-700 flex items-center">
                          <Package className="h-5 w-5 mr-2 text-green-500" />
                          Orders with Quote & Shipment
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-700">{quoteData.totalOrdersWithQuoteAndShipment}</div>
                        <p className="text-sm text-gray-500 mt-1">📦 Total orders processed</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-700 flex items-center">
                          <BarChart className="h-5 w-5 mr-2 text-purple-500" />
                          Average Quote Price
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-700">${quoteData.averageQuotePrice.toFixed(2)}</div>
                        <p className="text-sm text-gray-500 mt-1">📊 Average cost per order</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Filters and Export */}
                <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="carrier-filter">Carrier</Label>
                      <Select 
                        value={filters.carrier} 
                        onValueChange={(value) => handleFilterChange('carrier', value)}
                      >
                        <SelectTrigger id="carrier-filter" className="w-[180px]">
                          <SelectValue placeholder="Select carrier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Carriers</SelectItem>
                          {carriers.map(carrier => (
                            <SelectItem key={carrier} value={carrier}>{carrier}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country-filter">Country</Label>
                      <Select 
                        value={filters.country} 
                        onValueChange={(value) => handleFilterChange('country', value)}
                      >
                        <SelectTrigger id="country-filter" className="w-[180px]">
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Countries</SelectItem>
                          {countries.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Overall Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">Total Delivered</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{filteredData.overall.totalDelivered}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">On-Time Deliveries</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{filteredData.overall.onTimeDeliveries}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-gray-500">On-Time Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold">{filteredData.overall.onTimeRate}%</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts and Tables */}
                <Tabs defaultValue="charts" className="space-y-6">
                  <TabsList>
                    <TabsTrigger value="charts" className="flex items-center gap-2">
                      <BarChart2 className="h-4 w-4" />
                      Charts
                    </TabsTrigger>
                    <TabsTrigger value="tables">Tables</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="charts" className="space-y-6">
                    {/* By Carrier Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">On-Time Rate by Carrier</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-80">
                          {carrierChartData && <Bar data={carrierChartData} options={chartOptions} />}
                        </div>
                      </CardContent>
                    </Card>

                    {/* By Country Chart */}
                    {countryChartData && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">On-Time Rate by Country</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-80">
                            <Bar data={countryChartData} options={chartOptions} />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Pie Chart for Delivery Volume */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Delivery Volume by Carrier</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-80">
                          {volumePieChartData && (
                            <Pie 
                              data={volumePieChartData} 
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                  legend: {
                                    position: 'right',
                                  },
                                },
                              }} 
                            />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                  
                  <TabsContent value="tables">
                    {/* By Carrier Table */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Performance by Carrier</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Carrier</TableHead>
                                <TableHead className="text-right">Total Delivered</TableHead>
                                <TableHead className="text-right">On-Time Deliveries</TableHead>
                                <TableHead className="text-right">On-Time Rate (%)</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredData.byCarrier.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium">{item.carrier}</TableCell>
                                  <TableCell className="text-right">{item.totalDelivered}</TableCell>
                                  <TableCell className="text-right">{item.onTimeDeliveries}</TableCell>
                                  <TableCell className="text-right">{item.onTimeRate}%</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* By Country Table */}
                    {filteredData.byCountry.length > 0 && (
                      <Card className="mt-6">
                        <CardHeader>
                          <CardTitle className="text-lg">Performance by Country</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Country</TableHead>
                                  <TableHead className="text-right">Total Delivered</TableHead>
                                  <TableHead className="text-right">On-Time Deliveries</TableHead>
                                  <TableHead className="text-right">On-Time Rate (%)</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {filteredData.byCountry.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{item.country}</TableCell>
                                    <TableCell className="text-right">{item.totalDelivered}</TableCell>
                                    <TableCell className="text-right">{item.onTimeDeliveries}</TableCell>
                                    <TableCell className="text-right">{item.onTimeRate}%</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </CardContent>
        </Card>
        <ToastContainer />
      </div>
    </div>
  );
}

export default ShipmentAnalyticsReport;
